import {
  Component,
  OnDestroy,
  OnInit,
  CUSTOM_ELEMENTS_SCHEMA,
  inject
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { WordViewerOpenOptions, WordViewerService } from '../../../core/services/word-viewer.service';
import { StorageService } from '../../../core/services/storage.service';
import { NotificationService } from '../../../core/services/notification.service';
import { buildOfficeOnlineEmbedUrl } from '../../utils/word-mime.util';

@Component({
  selector: 'app-word-viewer-modal',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './word-viewer-modal.component.html',
  styleUrls: ['./word-viewer-modal.component.css'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class WordViewerModalComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly wordViewerService = inject(WordViewerService);
  private readonly storageService = inject(StorageService);
  private readonly notificationService = inject(NotificationService);
  private readonly translateService = inject(TranslateService);
  private readonly sanitizer = inject(DomSanitizer);

  open = false;
  title = '';
  wordUrl: string | null = null;
  safeEmbedUrl: SafeResourceUrl | null = null;
  loading = false;

  ngOnInit(): void {
    this.wordViewerService.request$
      .pipe(takeUntil(this.destroy$))
      .subscribe((request) => {
        if (!request) {
          this.reset();
          return;
        }
        this.loadRequest(request);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onOverlayClick(): void {
    this.close();
  }

  close(): void {
    this.wordViewerService.close();
  }

  openInBrowser(): void {
    if (this.wordUrl) {
      window.open(this.wordUrl, '_blank', 'noopener,noreferrer');
    }
  }

  private loadRequest(request: WordViewerOpenOptions): void {
    this.open = true;
    this.title = request.title;
    this.wordUrl = null;
    this.safeEmbedUrl = null;
    this.loading = true;

    if (request.wordUrl) {
      this.setWordUrl(request.wordUrl);
      this.loading = false;
      return;
    }

    if (!request.s3Key) {
      this.handleLoadError();
      return;
    }

    this.storageService
      .generateDownloadUrl(request.s3Key, { inline: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success && response.data?.downloadUrl) {
            this.setWordUrl(response.data.downloadUrl);
          } else {
            this.handleLoadError();
          }
          this.loading = false;
        },
        error: () => {
          this.handleLoadError();
          this.loading = false;
        }
      });
  }

  private setWordUrl(url: string): void {
    this.wordUrl = url;
    this.safeEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      buildOfficeOnlineEmbedUrl(url)
    );
  }

  private handleLoadError(): void {
    this.reset();
    this.notificationService.error(
      this.translateService.instant('dashboard.common.error'),
      this.translateService.instant('dashboard.common.wordViewer.loadError')
    );
  }

  private reset(): void {
    this.open = false;
    this.title = '';
    this.wordUrl = null;
    this.safeEmbedUrl = null;
    this.loading = false;
  }
}
